import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { environment } from '../../environments/environment';

@Injectable()
export class MaintenanceService {
    constructor(private apiService: ApiService) {}

    /**
     * Check if maintenance mode is currently active
     * Returns false if API call fails (fail-safe mode)
     */
    public async isMaintenanceActive(): Promise<boolean> {
        // Check for environment override first
        if (environment.overrideMaintenanceMode !== null && environment.overrideMaintenanceMode !== undefined) {
            console.log('🔧 Maintenance mode overridden by environment:', environment.overrideMaintenanceMode);
            return environment.overrideMaintenanceMode;
        }

        try {
            const showMaintenance = await this.apiService.getSetting('showMaintenance');
            const timerEnddate = await this.apiService.getSetting('timerEnddate');

            // If showMaintenance is not set or false, maintenance is not active
            if (!showMaintenance) {
                return false;
            }

            // If timerEnddate is set, check if current date has passed the end date
            if (timerEnddate) {
                const endDate = this.parseDate(timerEnddate);
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0); // Reset time to compare only dates

                // If current date is >= end date, maintenance should be disabled
                if (currentDate >= endDate) {
                    return false;
                }
            }

            return true;
        } catch (error) {
            // Fail-safe: if API call fails, allow site access
            console.error('Failed to fetch maintenance settings:', error);
            return false;
        }
    }

    /**
     * Get the maintenance end date from API
     * Returns null if not set or if API call fails
     */
    public async getMaintenanceEndDate(): Promise<string | null> {
        try {
            return await this.apiService.getSetting('timerEnddate') || null;
        } catch (error) {
            console.error('Failed to fetch maintenance end date:', error);
            return null;
        }
    }

    /**
     * Parse date string in DD/MM/YYYY format to Date object
     * @param dateString - Date in format "DD/MM/YYYY"
     * @returns Date object with time set to 00:00:00
     */
    private parseDate(dateString: string): Date {
        const parts = dateString.split('/');
        if (parts.length !== 3) {
            throw new Error(`Invalid date format: ${dateString}. Expected DD/MM/YYYY`);
        }

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JavaScript Date
        const year = parseInt(parts[2], 10);

        const date = new Date(year, month, day);
        date.setHours(0, 0, 0, 0); // Reset time to midnight

        return date;
    }
}
